import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'

export interface Breadcrumb { key: string; to: string; tab?: string }
type PageRoute = Pick<RouteLocationNormalizedLoaded, 'path' | 'params' | 'query'>

const pageKeys: Record<string, string> = {
  feedback: 'feedback',
  'coach-journal': 'journals', 'coach-journal-2': 'journals',
  journal: 'journals', 'journal-2': 'journals',
  'activities-2': 'activities2',
  planner: 'sessionPlanner',
  'decision-room': 'decisionRoom',
  'prototype-decision-room': 'prototypeDecisionRoom',
  'program-settings': 'programSettings', setup: 'settings', 'import-athletes': 'importParticipants',
  athletes: 'participants', capture: 'capture', manageuser: 'account', completeaccount: 'completeAccount',
  analytics: 'analytics', dashboard: 'dashboard', 'platform-maintenance': 'maintenance',
}

// Only accept a settings return destination within this exact event. Never use
// arbitrary URLs or another club/event's history as a parent.
export function settingsReturnPath(path: string, candidate: unknown): string | undefined {
  if (typeof candidate !== 'string') return
  const base = path.match(/^(\/clubhouse\/[^/]+\/season\/[^/]+\/tryout\/[^/]+)\/[^/]+$/)?.[1]
  if (!base || /\/(setup|program-settings)$/.test(path)) return
  const candidatePath = candidate.split(/[?#]/)[0]
  if (candidatePath === `${base}/setup` || candidatePath === `${base}/program-settings`) return candidate
}

// Planner links retain only a local origin in the same event.
export function plannerOriginPath(base: string, candidate: unknown): string {
  if (typeof candidate === 'string' && !candidate.includes('\\') && !/[\r\n]/.test(candidate)) {
    const path = candidate.split(/[?#]/)[0]
    if ([base, `${base}/setup`, `${base}/program-settings`].includes(path || '')) return candidate
  }
  return `${base}?section=sessions`
}

export function breadcrumbsFor(route: PageRoute, returnTo?: unknown): Breadcrumb[] {
  const crumbs: Breadcrumb[] = [{ key: 'home', to: '/' }]
  const { clubId, seasonId, tryoutId } = route.params
  if (clubId) crumbs.push({ key: 'club', to: `/clubhouse/${encodeURIComponent(String(clubId))}` })
  if (clubId && seasonId) crumbs.push({ key: 'season', to: `${crumbs[1]!.to}/season/${encodeURIComponent(String(seasonId))}` })
  if (clubId && seasonId && tryoutId) crumbs.push({ key: 'event', to: `${crumbs[2]!.to}/tryout/${encodeURIComponent(String(tryoutId))}` })
  const eventBase = crumbs.find(crumb => crumb.key === 'event')?.to
  if (eventBase && (route.path === `${eventBase}/session-planner` || /^\/sessions\/[^/]+\/planner$/.test(route.path.slice(eventBase.length)))) {
    const origin = plannerOriginPath(eventBase, route.query.from || returnTo)
    const originPath = origin.split(/[?#]/)[0]
    if (originPath === eventBase) crumbs[crumbs.length - 1]!.to = origin
    else crumbs.push({ key: originPath?.endsWith('/program-settings') ? 'programSettings' : 'settings', to: origin })
    crumbs.push({ key: 'sessionPlanner', to: `${eventBase}/session-planner?${new URLSearchParams({ from: origin })}` })
    if (route.params.sessionId) crumbs.push({ key: 'sessionPlan', to: route.path })
    return crumbs
  }
  if(route.path.includes('/episodes')) {
    crumbs.push({ key: 'episodes', to: `${crumbs[3]!.to}/episodes` })
    if(route.params.episodeId || route.path.endsWith('/episodes/new')) crumbs.push({ key: route.params.episodeId ? 'episode' : 'newEpisode', to: route.path })
    return crumbs
  }
  const parent = settingsReturnPath(route.path, returnTo)
  if (parent) crumbs.push({ key: parent.split('?')[0]!.endsWith('/program-settings') ? 'programSettings' : 'settings', to: parent })
  const leaf = route.path.split('/').filter(Boolean).slice(-1)[0] || ''
  if (pageKeys[leaf]) crumbs.push({ key: pageKeys[leaf]!, to: route.path })
  if ((leaf === 'setup' || leaf === 'program-settings') && typeof route.query.tab === 'string') {
    crumbs.push({ key: 'section', to: route.path, tab: `${leaf === 'setup' ? 'tryoutSetup' : 'programSettings'}.tabs.${route.query.tab}` })
  }
  return crumbs
}

export function installNavigationContext(router: Router) {
  let pendingReturn: string | undefined
  router.beforeEach((to, from) => {
    pendingReturn = settingsReturnPath(to.path, from.fullPath)
      || settingsReturnPath(to.path, router.options.history.state.settingsReturnTo)
  })
  return router.afterEach((to, _from, failure) => {
    if (failure) return
    const state = router.options.history.state
    const returnTo = settingsReturnPath(to.path, state.settingsReturnTo) || settingsReturnPath(to.path, state.back) || pendingReturn
    if (returnTo !== state.settingsReturnTo) {
      router.options.history.replace(to.fullPath, { ...state, settingsReturnTo: returnTo || null })
    }
  })
}

// Reuse the previous entry when it is the desired parent, avoiding A → B → A
// loops. Deep links still have a predictable in-app destination.
export function returnToParent(router: Router, destination: string) {
  if (router.options.history.state.back === destination) router.back()
  else return router.push(destination)
}
